import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Minus, ArrowRight, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { BRAND } from "@/lib/constants";

const FAQ = [
  {
    q: "Hvordan fungerer samarbejdet?",
    a: "Vi starter med en gratis gennemgang af din bolig - online eller på stedet. Herefter opretter/optimerer vi din annonce, sætter dynamisk prissætning op og markedsfører boligen på alle relevante bookingplatforme samt direkte her via vores egne kanaler. Fra dag ét håndterer vi gæstekommunikation, kalender, rengøring og løbende drift. Du modtager månedlig udbetaling og rapport.",
  },
  {
    q: "Hvem modtager lejeindtægten?",
    a: "Du modtager lejeindtægten direkte fra bookingplatformene, minus vores aftalte honorar. Alt fremgår tydeligt af din månedlige rapport - fuld gennemsigtighed, ingen skjulte gebyrer.",
  },
  {
    q: "Er der binding?",
    a: "Ja, vi arbejder med en standard kontraktperiode, så vi kan investere i opsætning, professionelle billeder, prisoptimering og opbygning af gode reviews. Den præcise længde aftaler vi i fællesskab på vores første møde.",
  },
  {
    q: "Kan I hjælpe, hvis jeg allerede udlejer selv?",
    a: "Ja - vi overtager gerne din eksisterende annonce og optimerer den. Ofte kan vi løfte belægningen mærkbart alene ved bedre billeder, ny prissætning og aktiv markedsføring. Vi har fx løftet en case med +133 % højere belægning efter overtagelse.",
    a: "Ja - vi overtager gerne din eksisterende annonce og optimerer den. Ofte kan vi løfte belægningen mærkbart alene ved bedre billeder, ny prissætning og aktiv markedsføring. Vi har fx løftet en case med +133 % højere belægning efter overtagelse.",
  },
  {
    q: "Hvad koster det?",
    a: "Der er 0 kr. i opstart. Vi tager en provision af din lejeindtægt - så du betaler kun, når din bolig tjener penge. Den præcise sats aftaler vi ud fra boligens potentiale og ambitionsniveau.",
  },
  {
    q: "Hvor bliver min bolig markedsført?",
    a: "Vi lister din bolig på alle relevante bookingplatforme som standard - og tager også imod direkte bookinger her på hjemmesiden, via egne kanaler, sociale medier og virksomhedsaftaler. Kalendere synkroniseres, så du aldrig får dobbeltbookinger.",
  },
  {
    q: "Hvor hurtigt kan I komme i gang?",
    a: "Inden for 7 dage fra første møde har vi typisk din annonce klar og aktiv på alle relevante platforme - inklusive professionelle billeder, dynamisk prissætning og gæstekommunikation opsat.",
  },
  {
    q: "Hvordan håndterer I rengøring og linned?",
    a: "Vi koordinerer rengøring mellem hvert ophold med faste samarbejdspartnere, der kender vores standard. Linned, håndklæder og forbrugsvarer er en del af pakken - din bolig står altid klar i hotelstandard.",
  },
  {
    q: "Hvad sker der, hvis en gæst forårsager skade?",
    a: "Vi screener gæster grundigt forud og er dækket af de gældende forsikringsordninger på bookingplatformene samt egne procedurer for inspektion efter hvert ophold. Skader rapporteres og håndteres af os direkte med både gæst og forsikring.",
  },
  {
    q: "Kan jeg selv bruge boligen, når jeg ønsker?",
    a: "Selvfølgelig. Du blokerer blot datoerne i kalenderen - så bookes der ingen gæster i den periode. Det er din bolig; vi gør den blot tilgængelig for gæster, når du ikke selv bruger den.",
  },
  {
    q: "Hvor arbejder I geografisk?",
    a: "Vi har base i Sæby og arbejder med boliger i hele Danmark - fra byboliger til sommerhusområderne. Hver bolig vurderes individuelt.",
  },
  {
    q: "Hvordan kommer jeg i gang?",
    a: "Book et gratis, uforpligtende møde i kalenderen - eller ring direkte på +45 20 83 93 96. Vi gennemgår din bolig og viser dig præcis, hvordan vi kan øge belægningen og gøre udlejningen næsten 100 % passiv.",
  },
];

function FaqItem({ q, a, i, open, setOpen }) {
  const isOpen = open === i;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: i * 0.03 }}
      className="border-b border-black/10"
      data-testid={`faq-item-${i}`}
    >
      <button
        type="button"
        onClick={() => setOpen(isOpen ? -1 : i)}
        className="w-full flex items-start justify-between gap-6 py-6 sm:py-8 text-left group"
        data-testid={`faq-toggle-${i}`}
      >
        <span className="flex items-start gap-5 sm:gap-7">
          <span
            className="font-serif text-base sm:text-xl text-[#D4AF37] font-light leading-none mt-1 sm:mt-2 tabular-nums shrink-0 w-7 sm:w-9"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            {String(i + 1).padStart(2, "0")}
          </span>
          <span
            className="font-serif text-xl sm:text-2xl lg:text-[1.75rem] text-[#111111] font-normal leading-snug group-hover:text-[#C9A227] transition-colors"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            {q}
          </span>
        </span>
        <span className="shrink-0 mt-1 sm:mt-2 w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-[#111111]/15 flex items-center justify-center group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-all">
          {isOpen ? (
            <Minus size={16} strokeWidth={1.5} className="text-[#111111]" />
          ) : (
            <Plus size={16} strokeWidth={1.5} className="text-[#111111]" />
          )}
        </span>
      </button>

      <motion.div
        initial={false}
        animate={{
          height: isOpen ? "auto" : 0,
          opacity: isOpen ? 1 : 0,
        }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        <div className="pb-7 sm:pb-9 pl-[3rem] sm:pl-[4rem] pr-2 sm:pr-12">
          <p className="text-[#555555] font-light leading-relaxed text-base sm:text-lg max-w-3xl">
            {a}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <div className="bg-white text-[#222222]" data-testid="faq-page">
      <Header />

      {/* Hero */}
      <section className="relative bg-[#111111] text-white pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[34rem] h-[34rem] glow-gold pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.05] select-none">
          <span
            className="font-serif text-[26vw] leading-none italic"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            FAQ
          </span>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="overline mb-5 text-[10px] sm:text-xs" style={{ color: "#D4AF37" }}>
            <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
            Ofte stillede spørgsmål
          </p>
          <h1
            className="font-serif text-[2.5rem] sm:text-6xl lg:text-[5.5rem] text-white font-light tracking-tight leading-[1.02] max-w-4xl"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            De spørgsmål vi oftest{" "}
            <span className="italic text-[#D4AF37]">får</span>.
          </h1>
          <p className="mt-6 text-white/70 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
            Står dit spørgsmål ikke på listen? Ring direkte på{" "}
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="text-white link-underline"
            >
              {BRAND.phone}
            </a>{" "}
            eller skriv til{" "}
            <a href={`mailto:${BRAND.email}`} className="text-white link-underline">
              {BRAND.email}
            </a>
            .
          </p>
        </div>
      </section>

      {/* FAQ list */}
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          {FAQ.map((item, i) => (
            <FaqItem
              key={i}
              i={i}
              q={item.q}
              a={item.a}
              open={open}
              setOpen={setOpen}
            />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F8F8F8] py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="overline mb-5 text-[10px] sm:text-xs">
                <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
                Stadig i tvivl?
              </p>
              <h2
                className="font-serif text-[2rem] sm:text-4xl lg:text-5xl text-[#111111] font-light tracking-tight leading-[1.1]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                Tag fat i Gustav - det{" "}
                <span className="italic text-[#C9A227]">koster intet</span>.
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="faq-call-btn"
                className="inline-flex items-center gap-3 border border-[#111111] text-[#111111] px-6 py-4 text-xs uppercase tracking-[0.18em] hover:bg-[#111111] hover:text-white transition-colors duration-500"
              >
                <Phone size={16} strokeWidth={1.5} />
                <span>{BRAND.phone}</span>
              </a>
              <Link
                to="/om-os#book-form"
                data-testid="faq-book-btn"
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#111111] px-6 py-4 text-xs uppercase tracking-[0.18em] hover:bg-[#111111] hover:text-white transition-colors duration-500"
              >
                <span>Book et møde</span>
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
